import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { themesApi } from '../api/client';

interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  muted: string;
}

interface ThemeTypography {
  headingFont: string;
  bodyFont: string;
  baseSize: string;
}

interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  typography: ThemeTypography;
  features: string[];
}

interface ThemeContextValue {
  currentTheme: string;
  themeConfig: ThemeConfig | null;
  setCurrentTheme: (theme: string) => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Safe version of useTheme that returns default values when not in ThemeProvider
// eslint-disable-next-line react-refresh/only-export-components
export const useThemeSafe = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default theme values when not in ThemeProvider
    return {
      currentTheme: 'standard',
      themeConfig: null,
      setCurrentTheme: () => {},
      isLoading: false,
    };
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  theme?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, theme = 'standard' }) => {
  const [currentTheme, setCurrentTheme] = useState(theme);
  const [themeConfig, setThemeConfig] = useState<ThemeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  console.log('🎨 ThemeProvider: Initialized with theme:', theme);

  useEffect(() => {
    loadThemeConfig(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    if (theme !== currentTheme) {
      setCurrentTheme(theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const loadThemeConfig = async (themeId: string) => {
    try {
      setIsLoading(true);
      const config = await themesApi.get(themeId);
      setThemeConfig(config);

      // Apply theme CSS variables
      if (config?.colors) {
        const root = document.documentElement;
        root.style.setProperty('--theme-primary', config.colors.primary);
        root.style.setProperty('--theme-secondary', config.colors.secondary);
        root.style.setProperty('--theme-accent', config.colors.accent);
        root.style.setProperty('--theme-background', config.colors.background);
        root.style.setProperty('--theme-text', config.colors.text);
        root.style.setProperty('--theme-muted', config.colors.muted);
      }

      // Typography CSS variables are applied via useEffect to the scoped container
      // This prevents theme fonts from affecting the website builder's own UI
    } catch (error) {
      console.error('Failed to load theme config:', error);
      // Use default theme config if loading fails
      setThemeConfig({
        id: 'standard',
        name: 'Standard',
        description: 'Classic professional design',
        colors: {
          primary: '#4F46E5',
          secondary: '#10B981',
          accent: '#F59E0B',
          background: '#FFFFFF',
          text: '#1F2937',
          muted: '#6B7280',
        },
        typography: {
          headingFont: 'Inter, system-ui, sans-serif',
          bodyFont: 'Inter, system-ui, sans-serif',
          baseSize: '16px',
        },
        features: ['Clean Layout', 'Professional Typography', 'Balanced Spacing'],
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Apply typography CSS variables when themeConfig changes and container is available
  useEffect(() => {
    if (themeConfig?.typography && containerRef.current) {
      containerRef.current.style.setProperty(
        '--theme-heading-font',
        themeConfig.typography.headingFont
      );
      containerRef.current.style.setProperty('--theme-body-font', themeConfig.typography.bodyFont);
      containerRef.current.style.setProperty('--theme-base-size', themeConfig.typography.baseSize);
    }
  }, [themeConfig]);

  return (
    <ThemeContext.Provider value={{ currentTheme, themeConfig, setCurrentTheme, isLoading }}>
      <div ref={containerRef} className="theme-content">
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
